import { useOrganization } from '@features/organizations'
import { useFlashState } from '@st/react-util/use-flash-state'
import {
  RETURN_TYPES,
  buildSchemaId,
  type ReturnType,
  type TaxYear
} from '@st/tax-folder'
import {
  Button,
  CopyIcon,
  LinkIcon,
  Select,
  SendIcon,
  Wizard,
  WizardIcons
} from '@st/theme'
import type { ReactNode } from 'react'
import { match, P } from '@st/pattern'
import { useEmailsManagedBy } from '~/features/app-hooks'
import { clientMagicLink, MAGIC_LINK_PORTALS, type MagicLinkPortal } from '~/features/routes'
import { useProcess, useSelect } from '~/store'
import type { MagicLinkDelivery, MagicLinksMessage, MagicLinksState } from './magic-links-module'

const MAGIC_LINK_TAX_YEARS = [2026, 2025, 2024, 2023] as TaxYear[]
const MAGIC_LINK_TAX_YEAR_OPTIONS = MAGIC_LINK_TAX_YEARS.map(String)

const PORTAL_OPTIONS: Record<MagicLinkPortal, { label: string; logo: string | null }> = {
  karbon: { label: 'Karbon', logo: '/icons/karbon-logo.png' },
  canopy: { label: 'Canopy', logo: '/logos/canopy.svg' },
  tax_dome: { label: 'TaxDome', logo: '/logos/tax-dome.svg' },
  financial_cents: {
    label: 'Financial Cents',
    logo: '/logos/financial-cents.png'
  },
  firm360: { label: 'Firm360', logo: '/logos/firm360.png' },
  qount: { label: 'Qount', logo: '/logos/qount.svg' },
  double: { label: 'Double', logo: '/logos/double.png' },
  anchor: { label: 'Anchor', logo: '/logos/anchor.jpg' },
  other: { label: 'Other', logo: null }
}

export function MagicLinksPage({ onClose }: { onClose: () => void }) {
  const magicLinks = useProcess('magicLinks')
  const state = useSelect('magicLinks', (state) => state)
  const organization = useOrganization()
  const emailsManagedBy = useEmailsManagedBy()

  return match(state)
    .with({ step: 'idle' }, () => null)
    .with({ step: 'selectSharingMethod' }, () => (
      <SharingMethodPage send={magicLinks.send} onClose={onClose} />
    ))
    .with({ step: 'stanfordtaxGuidance' }, () => (
      <StanfordTaxGuidancePage
        emailsManagedBy={emailsManagedBy}
        send={magicLinks.send}
        onClose={onClose}
      />
    ))
    .with({ step: 'selectPortal' }, () => (
      <PortalSelectorPage send={magicLinks.send} onClose={onClose} />
    ))
    .with({ step: 'karbonGuidance' }, () => (
      <KarbonGuidancePage send={magicLinks.send} onClose={onClose} />
    ))
    .with({ step: 'selectSchema' }, (state) => (
      <SchemaSelectorPage state={state} send={magicLinks.send} onClose={onClose} />
    ))
    .with({ step: 'copyLinks' }, (state) => (
      <CopyLinksPage
        state={state}
        organizationSlug={organization.slug}
        send={magicLinks.send}
        onClose={onClose}
      />
    ))
    .exhaustive()
}

function SharingMethodPage({
  send,
  onClose
}: {
  send: (message: MagicLinksMessage) => void
  onClose: () => void
}) {
  return (
    <Wizard.Scaffold breadcrumb="Magic links" onClose={onClose}>
      <Wizard.Main title="How would you like to share the magic link with clients?">
        <Wizard.OptionsColumn>
          <Wizard.LargeOptionButton
            icon={StanfordTaxIcon}
            caption="Select this option if you'd like to send the links from within StanfordTax."
            onClick={() => send({ type: 'sharingMethodSelected', method: 'stanfordtax' })}
          >
            I'd like StanfordTax to email the links for me
          </Wizard.LargeOptionButton>
          <Wizard.LargeOptionButton
            icon={EmailIcon}
            caption="Select this option if you'd like to email the links yourself."
            onClick={() => send({ type: 'sharingMethodSelected', method: 'selfEmail' })}
          >
            I'd like to email the links myself
          </Wizard.LargeOptionButton>
          <Wizard.LargeOptionButton
            icon={PortalIcon}
            caption="Select this option if you'd like to use the links in your client portal."
            onClick={() => send({ type: 'sharingMethodSelected', method: 'portal' })}
          >
            I'd like to share the links via my client portal
          </Wizard.LargeOptionButton>
        </Wizard.OptionsColumn>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function StanfordTaxGuidancePage({
  emailsManagedBy,
  send,
  onClose
}: {
  emailsManagedBy: 'stanfordtax' | 'karbon'
  send: (message: MagicLinksMessage) => void
  onClose: () => void
}) {
  const guidance = match(emailsManagedBy)
    .with(
      'stanfordtax',
      () =>
        'Go to the Binders page, select the clients you want to contact, and click “Send to client.”'
    )
    .with(
      'karbon',
      () =>
        'This organization uses Karbon for email delivery. Go to the Binders page, select the clients you want to contact, and click “Send to client” to review the delivery flow.'
    )
    .exhaustive()

  return (
    <GuidancePage
      title="Magic link not needed"
      body={`If you'd like StanfordTax to send the links for you, you do not need a magic link. ${guidance}`}
      icon={<StanfordTaxIcon className="size-14 object-contain" />}
      send={send}
      onClose={onClose}
    />
  )
}

function KarbonGuidancePage({
  send,
  onClose
}: {
  send: (message: MagicLinksMessage) => void
  onClose: () => void
}) {
  return (
    <GuidancePage
      title="Magic link not needed"
      body="StanfordTax has a deep integration with Karbon that automatically places each organizer in the Karbon for Clients portal. To use the integration, connect your StanfordTax account from Karbon's Connected Apps settings."
      icon={<img className="size-14 object-contain" src="/icons/karbon-logo.png" alt="" />}
      send={send}
      onClose={onClose}
    />
  )
}

function GuidancePage({
  title,
  body,
  icon,
  send,
  onClose
}: {
  title: string
  body: string
  icon: ReactNode
  send: (message: MagicLinksMessage) => void
  onClose: () => void
}) {
  return (
    <Wizard.Scaffold
      breadcrumb="Magic links"
      onClose={onClose}
      bottomNav={<BackButton send={send} />}
    >
      <Wizard.Notice title={title} icon={icon}>
        {body}
      </Wizard.Notice>
    </Wizard.Scaffold>
  )
}

function PortalSelectorPage({
  send,
  onClose
}: {
  send: (message: MagicLinksMessage) => void
  onClose: () => void
}) {
  return (
    <Wizard.Scaffold
      breadcrumb="Magic links"
      onClose={onClose}
      bottomNav={<BackButton send={send} />}
    >
      <Wizard.Main
        title="Which client portal do you use?"
        subtitle="We'll generate a link tailored for use in your portal's client tasks."
      >
        <Wizard.OptionsGrid>
          {MAGIC_LINK_PORTALS.map((portal) => {
            const option = PORTAL_OPTIONS[portal]
            const icon = match(option.logo)
              .with(P.string, (logo) => (
                <img className="size-14 object-contain" src={logo} alt="" />
              ))
              .with(null, () => <WizardIcons.EllipsisCircleIcon className="size-14" />)
              .exhaustive()

            return (
              <Wizard.OptionButton
                key={portal}
                icon={icon}
                onClick={() => send({ type: 'portalSelected', portal })}
              >
                {option.label}
              </Wizard.OptionButton>
            )
          })}
        </Wizard.OptionsGrid>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function SchemaSelectorPage({
  state,
  send,
  onClose
}: {
  state: Extract<MagicLinksState, { step: 'selectSchema' }>
  send: (message: MagicLinksMessage) => void
  onClose: () => void
}) {
  return (
    <Wizard.Scaffold
      breadcrumb="Magic links"
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button variant="default" onClick={() => send({ type: 'backRequested' })}>
            Back
          </Button>
          <Button variant="primary" onClick={() => send({ type: 'generateLinksRequested' })}>
            Generate links
          </Button>
        </Wizard.BottomNav>
      }
    >
      <Wizard.Main title="What year and return type do you want the magic link for?">
        <div className="flex w-[600px] flex-col gap-6">
          <label className="flex flex-col gap-2 text-base font-semibold">
            Tax year
            <Select
              value={String(state.taxYear)}
              options={MAGIC_LINK_TAX_YEAR_OPTIONS}
              buildValue={(year) => year}
              buildLabel={(year) => year}
              onChange={(taxYear) => {
                const selectedTaxYear = MAGIC_LINK_TAX_YEARS.find((year) => String(year) == taxYear)
                if (selectedTaxYear === undefined) return
                send({ type: 'taxYearSelected', taxYear: selectedTaxYear })
              }}
            />
          </label>
          <label className="flex flex-col gap-2 text-base font-semibold">
            Return type
            <Select
              value={state.returnType}
              options={RETURN_TYPES}
              buildValue={(returnType) => returnType}
              buildLabel={(returnType) => returnType.toUpperCase()}
              onChange={(returnType) => {
                if (returnType !== undefined) {
                  send({ type: 'returnTypeSelected', returnType: returnType as ReturnType })
                }
              }}
            />
          </label>
        </div>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function CopyLinksPage({
  state,
  organizationSlug,
  send,
  onClose
}: {
  state: Extract<MagicLinksState, { step: 'copyLinks' }>
  organizationSlug: string
  send: (message: MagicLinksMessage) => void
  onClose: () => void
}) {
  const schemaId = buildSchemaId(state.taxYear, state.returnType)
  const portal = portalForDelivery(state.delivery)
  const newClientLink = clientMagicLink({
    organizationSlug,
    schemaId,
    clientKind: 'new',
    portal
  })
  const returningClientLink = clientMagicLink({
    organizationSlug,
    schemaId,
    clientKind: 'returning',
    portal
  })

  return (
    <Wizard.Scaffold
      breadcrumb="Magic links"
      onClose={onClose}
      bottomNav={<BackButton send={send} />}
    >
      <Wizard.Main
        title="Copy your magic link below"
        subtitle="For returning clients, generate their organizers first, and then share the second link. The second link will take them to their personalized organizer."
      >
        <div className="flex w-[600px] flex-col items-stretch gap-6">
          <CopyLinkInput
            label="For new clients (blank organizer)"
            link={newClientLink}
            onCopy={(url) => send({ type: 'copyLinkRequested', url })}
          />
          <CopyLinkInput
            label="For returning clients"
            link={returningClientLink}
            onCopy={(url) => send({ type: 'copyLinkRequested', url })}
          />
        </div>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function CopyLinkInput({
  label,
  link,
  onCopy
}: {
  label: string
  link: string
  onCopy: (link: string) => void
}) {
  const [copied, setCopied] = useFlashState(false, 2000)

  return (
    <label className="flex flex-col gap-2 text-base">
      {label}
      <div className="flex flex-row gap-2">
        <input
          type="text"
          className="flex-1 rounded-md border-0 py-1.5 pl-3 pr-10 text-base text-gray-700 ring-1 ring-inset ring-gray-300"
          value={link}
          readOnly
        />
        <Button
          className="w-32"
          leadingIcon={<CopyIcon className="h-4 w-4" />}
          variant="primary"
          onClick={() => {
            setCopied(true)
            onCopy(link)
          }}
        >
          {copied ? 'Copied!' : 'Copy link'}
        </Button>
      </div>
    </label>
  )
}

function BackButton({ send }: { send: (message: MagicLinksMessage) => void }) {
  return (
    <Wizard.BottomNav>
      <Button variant="default" onClick={() => send({ type: 'backRequested' })}>
        Back
      </Button>
    </Wizard.BottomNav>
  )
}

function portalForDelivery(delivery: MagicLinkDelivery): MagicLinkPortal | undefined {
  if (delivery.type == 'portal') return delivery.portal
  return undefined
}

function StanfordTaxIcon({ className }: { className: string }) {
  return <img className={className} src="/logos/stanfordtax.png" alt="" />
}

function EmailIcon({ className }: { className: string }) {
  return (
    <span className={`${className} flex items-center justify-center`}>
      <SendIcon className="size-8" />
    </span>
  )
}

function PortalIcon({ className }: { className: string }) {
  return (
    <span className={`${className} flex items-center justify-center`}>
      <LinkIcon className="size-8" />
    </span>
  )
}
